/* CiteAb publication figures: progressive enhancement of the existing product gallery.
 * The vendor uses a closed shadow root. Read its public HTML response, never its
 * private component state. Parse only known figure records; do not execute HTML.
 * Original figures, captions, licence and source links remain on the lower widget.
 */
(function () {
  'use strict';
  if (window.biossPublicationGalleryV424) return;
  window.biossPublicationGalleryV424 = true;

  var WIDGET_ORIGIN = 'https://widget.citeab.com';
  var states = new WeakMap();
  var galleries = new WeakSet();
  var text = {};
  var dialog;
  var dialogReturn;
  var figureRequests = new Map();
  var pendingRequests = [];
  var activeRequests = 0;

  function el(tag, className, value) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (value) node.textContent = value;
    return node;
  }
  function clean(value) { return (value || '').replace(/\s+/g, ' ').trim(); }
  function citationCount(html, sku, company) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var widget = doc.querySelector('.widget_v2[data-citation-count][data-code][data-company]');
    var value = widget && widget.getAttribute('data-citation-count');
    return widget && widget.getAttribute('data-code').toLowerCase() === sku.toLowerCase() &&
      widget.getAttribute('data-company') === company && /^\d{1,9}$/.test(value || '') ? Number(value) : null;
  }
  function https(value, host) {
    try {
      var url = new URL(value);
      return url.protocol === 'https:' && !url.username && !url.password &&
        (!host || url.hostname === host) ? url.href : '';
    } catch (_) { return ''; }
  }
  function link(label, href) {
    var node = el('a', '', label);
    node.href = href;
    node.target = '_blank';
    node.rel = 'noopener noreferrer';
    return node;
  }
  function imageKey(value) {
    try {
      var url = new URL(value, window.location.href);
      // CiteAb signs thumbnail/full-size transformations differently; their final
      // public image ID is stable across formats. Do not invent transformed URLs.
      if (url.hostname === 'images.citeab.com') return url.hostname + '/' + url.pathname.split('/').pop().replace(/\.[^.]+$/, '');
      return url.origin + url.pathname;
    } catch (_) { return value; }
  }
  function parse(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var used = new Set();
    return Array.from(doc.querySelectorAll('.slide[data-id][data-image-url]')).map(function (slide) {
      var id = slide.getAttribute('data-id');
      var image = slide.querySelector('.image img');
      var full = https(image && image.getAttribute('data-src'), 'images.citeab.com') ||
        https(slide.getAttribute('data-image-url'), 'images.citeab.com');
      var fullCaption = slide.querySelector('.caption .full-text');
      var captionNode = fullCaption || slide.querySelector('.caption .text');
      var truncated = !fullCaption && captionNode && captionNode.querySelector('a[onclick]');
      if (captionNode) {
        captionNode = captionNode.cloneNode(true);
        captionNode.querySelectorAll('script,style,a[onclick]').forEach(function (node) { node.remove(); });
      }
      var caption = clean(captionNode && captionNode.textContent);
      var publication = slide.querySelector('.publication-title a');
      var source = https(publication && publication.getAttribute('href'), 'www.citeab.com');
      var title = clean(publication && publication.textContent);
      var license = slide.querySelector('.license');
      var licenseText = clean(license && license.textContent);
      var byline = slide.querySelector('p.publication');
      var journal = slide.querySelector('.publication-link a');
      var label = clean(slide.querySelector('h5') && slide.querySelector('h5').textContent);
      var thumb = Array.from(doc.querySelectorAll('.gallery-link[data-image-id]')).find(function (node) {
        return node.getAttribute('data-image-id') === id;
      });
      var thumbImage = thumb && thumb.querySelector('img');
      // Missing provenance is a contract change, not permission to show an
      // unattributed figure. A truncated caption is not a full scientific caption.
      if (!/^\d+$/.test(id || '') || !full || !caption || !title || !source || !licenseText ||
          truncated || used.has(id) || used.has(imageKey(full))) return null;
      used.add(id); used.add(imageKey(full));
      return {
        id: id, full: full, thumb: https(thumbImage && thumbImage.getAttribute('src'), 'images.citeab.com') || full,
        label: label, title: title, caption: caption, byline: clean(byline && byline.textContent),
        source: source, journal: https(journal && journal.getAttribute('href')),
        license: licenseText,
        licenseLinks: Array.from(license.querySelectorAll('a[href]')).map(function (a) {
          return { text: clean(a.textContent), href: https(a.getAttribute('href')) };
        }).filter(function (a) { return a.href && a.text; })
      };
    }).filter(Boolean);
  }
  async function figures(html, request, signal) {
    var records = parse(html);
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var carousel = doc.querySelector('.carousel[data-images-url][data-total-image-count]');
    if (!carousel) return records;
    var total = Number(carousel.getAttribute('data-total-image-count'));
    var offset = carousel.querySelectorAll('.gallery-link[data-image-id]').length;
    var more = https(carousel.getAttribute('data-images-url'), new URL(WIDGET_ORIGIN).hostname);
    if (!more || !offset || !Number.isInteger(total) || total <= offset) return records;
    var url = new URL(more);
    var codes;
    try { codes = JSON.parse(url.searchParams.get('code')); } catch (_) { return records; }
    if (url.origin !== WIDGET_ORIGIN || url.pathname !== '/citation_v2_widget_carousel_images' ||
        url.searchParams.get('key') !== request.searchParams.get('key') ||
        url.searchParams.get('company') !== request.searchParams.get('company') ||
        !Array.isArray(codes) || codes.length !== 1 || codes[0] !== request.searchParams.get('code')) return records;
    // Follow the vendor's current public pagination contract, with a bounded
    // total request budget. If a later page fails, retain validated first pages.
    for (var batch = 0; offset < total && batch < 40; batch += 1) {
      try {
        url.searchParams.set('from', String(offset));
        var response = await fetch(url.href, { mode: 'cors', credentials: 'omit', signal: signal });
        if (!response.ok) break;
        var body = await response.text();
        if (body.length > 5000000) break;
        var payload = JSON.parse(body);
        if (typeof payload.gallery !== 'string' || typeof payload.carousel !== 'string') break;
        var next = parse(payload.carousel + payload.gallery);
        var nextDoc = new DOMParser().parseFromString(payload.carousel, 'text/html');
        var count = nextDoc.querySelectorAll('.gallery-link[data-image-id]').length;
        if (!count) break;
        next.forEach(function (record) {
          if (!records.some(function (existing) { return existing.id === record.id || imageKey(existing.full) === imageKey(record.full); })) records.push(record);
        });
        offset += count;
      } catch (_) { break; }
    }
    return records;
  }
  function provenance(record, expanded) {
    var box = el('div', 'bioss-published-provenance');
    box.appendChild(el('p', 'bioss-published-heading', text.research + (record.label ? ' · ' + record.label : '')));
    box.appendChild(el('p', 'bioss-published-title', record.title));
    if (record.byline) box.appendChild(el('p', 'bioss-published-byline', record.byline));
    var details = el('details', 'bioss-published-caption');
    if (expanded) details.open = true;
    details.appendChild(el('summary', '', text.caption));
    details.appendChild(el('p', '', record.caption));
    box.appendChild(details);
    var license = el('p', 'bioss-published-license');
    var remainder = record.license;
    record.licenseLinks.forEach(function (a) {
      var index = remainder.indexOf(a.text);
      if (index < 0) return;
      license.appendChild(document.createTextNode(remainder.slice(0, index)));
      license.appendChild(link(a.text, a.href));
      remainder = remainder.slice(index + a.text.length);
    });
    license.appendChild(document.createTextNode(remainder));
    box.appendChild(license);
    var sources = el('div', 'bioss-published-sources');
    sources.appendChild(link(text.source, record.source));
    if (record.journal) sources.appendChild(link(text.journal, record.journal));
    box.appendChild(sources);
    return box;
  }
  function photo(record, thumbnail, deferred) {
    var image = el('img');
    if (deferred) {
      // Use the incumbent Slick on-demand loader. Opacity-hidden Slick slides
      // can otherwise defeat native loading=lazy and fetch every full image.
      image.setAttribute('data-lazy', record.full);
      image.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
    } else image.src = thumbnail ? record.thumb : record.full;
    // Some incumbent templates put image.alt into .html(). Use trusted locale
    // text here, never remote captions. Full scientific text is rendered safely
    // with textContent in the associated figure caption.
    image.alt = thumbnail ? '' : text.figure;
    image.loading = 'lazy';
    image.decoding = 'async';
    image.addEventListener('error', function () {
      if (!thumbnail) {
        image.hidden = true;
        image.parentNode.appendChild(el('p', 'bioss-published-unavailable', text.unavailable));
      }
    }, { once: true });
    return image;
  }
  function openFigure(record, opener) {
    if (!dialog) {
      dialog = el('dialog', 'bioss-published-dialog');
      document.body.appendChild(dialog);
      dialog.addEventListener('close', function () {
        if (dialogReturn && dialogReturn.isConnected) dialogReturn.focus({ preventScroll: true });
      });
      dialog.addEventListener('click', function (event) {
        if (event.target === dialog) dialog.close();
      });
    }
    if (!dialog.showModal) { window.open(record.full, '_blank', 'noopener'); return; }
    dialog.replaceChildren();
    dialogReturn = opener;
    var close = el('button', 'bioss-published-close', text.close);
    close.type = 'button';
    close.addEventListener('click', function () { dialog.close(); });
    dialog.appendChild(close);
    var figure = el('figure');
    figure.appendChild(photo(record, false));
    var caption = el('figcaption');
    caption.appendChild(provenance(record, true));
    figure.appendChild(caption);
    dialog.appendChild(figure);
    dialog.setAttribute('aria-label', text.figure);
    dialog.showModal();
    close.focus();
  }
  function slide(record, deferred) {
    var node = el('div', 'product-image bioss-published-slide');
    node.dataset.biossPublicationId = record.id;
    var figure = el('figure', 'bioss-published-figure');
    var zoom = el('button', 'bioss-published-zoom');
    zoom.type = 'button';
    zoom.setAttribute('aria-label', text.view + (record.label ? ': ' + record.label : ''));
    zoom.appendChild(photo(record, false, deferred));
    zoom.appendChild(el('span', 'bioss-published-zoom-label', text.view));
    zoom.addEventListener('click', function () { openFigure(record, zoom); });
    figure.appendChild(zoom);
    var caption = el('figcaption');
    caption.appendChild(provenance(record, false));
    figure.appendChild(caption);
    node.appendChild(figure);
    return node;
  }
  function arrow(label, direction, callback) {
    var button = el('button', 'bioss-published-arrow');
    button.type = 'button';
    button.setAttribute('aria-label', label);
    var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('aria-hidden', 'true');
    var path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', direction === -1 ? 'M15 5l-7 7 7 7' : 'M9 5l7 7-7 7');
    svg.appendChild(path); button.appendChild(svg);
    button.addEventListener('click', callback);
    return button;
  }
  function controls(total, first, go, host) {
    var toolbar = el('div', 'bioss-published-controls');
    toolbar.setAttribute('role', 'group');
    toolbar.setAttribute('aria-label', text.gallery);
    var jump = el('button', 'bioss-published-jump', text.research + ' (' + total + ')');
    jump.type = 'button'; jump.addEventListener('click', function () { go(first); });
    toolbar.appendChild(jump);
    var navigation = el('div', 'bioss-published-navigation');
    navigation.appendChild(arrow(text.previous, -1, function () { go(-1, true); }));
    var count = el('output', 'bioss-published-count');
    count.setAttribute('aria-live', 'polite');
    navigation.appendChild(count);
    navigation.appendChild(arrow(text.next, 1, function () { go(1, true); }));
    toolbar.appendChild(navigation);
    host.appendChild(toolbar);
    return function (index, size) { count.textContent = (index + 1) + ' / ' + size; };
  }
  function fallback(records, root) {
    var host = el('section', 'bioss-published-fallback');
    host.setAttribute('aria-label', text.research);
    var stage = el('div', 'bioss-published-stage');
    var current = 0;
    var slides = records.map(function (record, i) {
      var node = slide(record); node.hidden = i !== 0; stage.appendChild(node); return node;
    });
    host.appendChild(stage);
    var update = controls(records.length, 0, function (value, relative) {
      current = (relative ? current + value : value) + records.length;
      current %= records.length;
      slides.forEach(function (node, i) { node.hidden = current !== i; });
      update(current, records.length);
    }, host);
    update(0, records.length);
    var anchor = root.querySelector('.images-container');
    if (anchor) anchor.appendChild(host);
    else root.insertBefore(host, root.firstChild);
  }
  function integrate(records, root) {
    var main = root.querySelector('#product-featured-image');
    var $ = window.jQuery;
    var initialized = main && main.classList.contains('slick-initialized') && main.slick;
    if (!main || !main.children.length || (!initialized && (!$ || !$.fn.slick))) { fallback(records, root); return; }
    // Some installed storefront apps load a second jQuery. Use the initialized
    // Slick instance's original handle rather than rebinding it to that copy.
    var $main = initialized ? main.slick.$slider : $(main);
    var nativeCount = initialized ? $main.slick('getSlick').slideCount : main.children.length;
    // A non-initialized multi-image gallery may be owned by a different theme
    // implementation. Keep it intact and use the accessible companion fallback.
    if (!initialized && nativeCount > 1) { fallback(records, root); return; }
    var thumbs = root.querySelector('#product-thumbnails');
    if (!initialized) {
      main.classList.add('full-width');
      $main.slick({ slidesToShow: 1, slidesToScroll: 1, arrows: false, fade: true, adaptiveHeight: true, infinite: false });
    }
    records.forEach(function (record) {
      $main.slick('slickAdd', slide(record, true));
      if (thumbs && thumbs.slick && thumbs.classList.contains('slick-initialized')) {
        var thumb = el('div', 'product-image bioss-published-thumb');
        var button = el('button', 'product-image-inner');
        button.type = 'button';
        button.setAttribute('aria-label', text.figure + (record.label ? ': ' + record.label : ''));
        button.appendChild(photo(record, true)); thumb.appendChild(button);
        thumbs.slick.$slider.slick('slickAdd', thumb);
      }
    });
    var go = function (index, relative) {
      var slick = $main.slick('getSlick');
      var destination = relative ? slick.currentSlide + index : index;
      $main.slick('slickGoTo', (destination + slick.slideCount) % slick.slideCount);
    };
    var update = controls(records.length, nativeCount, go, main.parentNode);
    update($main.slick('slickCurrentSlide'), nativeCount + records.length);
    $main.on('afterChange.biossPublications', function (_, slick, index) { update(index, slick.slideCount); });
    $main.on('lazyLoaded.biossPublications', function () { $main.slick('setPosition'); });
    $main.on('lazyLoadError.biossPublications', function (_, slick, image) {
      var node = image && (image[0] || image);
      if (node && node.closest('.bioss-published-slide')) {
        node.hidden = true;
        node.parentNode.appendChild(el('p', 'bioss-published-unavailable', text.unavailable));
      }
    });
    main.addEventListener('toggle', function () { $main.slick('setPosition'); }, true);
    main.querySelectorAll('.bioss-published-slide img').forEach(function (image) {
      image.addEventListener('load', function () { $main.slick('setPosition'); }, { once: true });
    });
    $main.slick('setPosition');
  }
  function productGalleryRoot() {
    var gallery = document.querySelector('.single-product #product-featured-image');
    return gallery ? gallery.closest('.product-images') : document.querySelector('.single-product .product-images');
  }
  async function enhance(widget) {
    if (states.has(widget)) return;
    var root = productGalleryRoot();
    var config = document.querySelector('[data-bioss-publication-gallery-config]');
    if (!root || !config || galleries.has(root)) return;
    try { text = JSON.parse(config.textContent); } catch (_) { return; }
    if (!text.research || !text.figure) return;
    states.set(widget, true);
    galleries.add(root);
    widget.dataset.biossGalleryState = 'loading';
    var controller = new AbortController();
    var timeout = window.setTimeout(function () { controller.abort(); }, 12000);
    try {
      var url = new URL('/citation_v2_widget', WIDGET_ORIGIN);
      url.searchParams.set('key', widget.getAttribute('data-api-key') || '');
      url.searchParams.set('code', widget.getAttribute('data-product-id') || '');
      url.searchParams.set('company', widget.getAttribute('data-company') || '');
      var response = await fetch(url.href, { mode: 'cors', credentials: 'omit', signal: controller.signal });
      if (!response.ok) throw new Error('CiteAb response unavailable');
      var html = await response.text();
      if (html.length > 5000000) throw new Error('CiteAb response exceeds bounds');
      if (!widget.isConnected) return;
      var total = citationCount(html, url.searchParams.get('code'), url.searchParams.get('company'));
      var label = document.querySelector('[data-bioss-pdp-citation-text]');
      if (label && total !== null) {
        if (!label.dataset.baseLabel) label.dataset.baseLabel = label.textContent;
        label.textContent = label.dataset.baseLabel + ' (' + total + ')';
      }
      var records = await figures(html, url, controller.signal);
      root = productGalleryRoot();
      if (!root || !root.isConnected || !widget.isConnected) return;
      var nativeKeys = new Set(Array.from(root.querySelectorAll('#product-featured-image img')).map(function (image) { return imageKey(image.src); }));
      records = records.filter(function (record) { return !nativeKeys.has(imageKey(record.full)); });
      if (widget.getAttribute('data-product-id') !== url.searchParams.get('code')) throw new Error('CiteAb product changed during request');
      if (!records.length) { widget.dataset.biossGalleryState = 'no-images'; return; }
      integrate(records, root);
      widget.dataset.biossGalleryState = 'ready';
      widget.dataset.biossGalleryFigures = String(records.length);
    } catch (_) {
      // Never hide/replace native photos or change the vendor widget on failure.
      widget.dataset.biossGalleryState = 'unavailable';
    } finally { window.clearTimeout(timeout); }
  }
  function start() {
    document.querySelectorAll('[data-citeab-citations-v2-widget]').forEach(enhance);
  }
  function configure() {
    var config = document.querySelector('[data-bioss-publication-gallery-config]');
    if (config) {
      try { text = JSON.parse(config.textContent); } catch (_) {}
    }
    return text;
  }
  function drain() {
    if (activeRequests >= 2 || !pendingRequests.length) return;
    activeRequests += 1;
    var job = pendingRequests.shift();
    job().finally(function () { activeRequests -= 1; drain(); });
    drain();
  }
  function loadFigures(settings) {
    var sku = clean(settings && settings.sku);
    if (!sku || sku.length > 160 || !settings.key || settings.company !== 'bioss') return Promise.resolve([]);
    var identity = JSON.stringify([settings.company, settings.key, sku]);
    if (figureRequests.has(identity)) return figureRequests.get(identity);
    var request = new Promise(function (resolve, reject) {
      pendingRequests.push(async function () {
        var controller = new AbortController();
        var timer = window.setTimeout(function () { controller.abort(); }, 12000);
        try {
          var url = new URL('/citation_v2_widget', WIDGET_ORIGIN);
          url.searchParams.set('key', settings.key);
          url.searchParams.set('code', sku);
          url.searchParams.set('company', settings.company);
          var response = await fetch(url.href, { mode: 'cors', credentials: 'omit', signal: controller.signal });
          if (!response.ok) throw new Error('Publication figures unavailable');
          var html = await response.text();
          if (html.length > 5000000) throw new Error('Publication response exceeds bounds');
          var records = await figures(html, url, controller.signal);
          records.citationCount = citationCount(html, sku, settings.company);
          resolve(records);
        } catch (error) {
          figureRequests.delete(identity);
          reject(error);
        } finally { window.clearTimeout(timer); }
      });
      drain();
    });
    figureRequests.set(identity, request);
    return request;
  }
  window.BiossPublicationMedia = {
    load: loadFigures,
    configure: configure,
    provenance: provenance,
    open: openFigure,
    imageKey: imageKey
  };
  configure();
  // DOMContentLoaded is later than the incumbent jQuery gallery ready handlers.
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  document.addEventListener('shopify:section:load', start);
})();
